import { Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { usePWA } from '@/hooks/usePWA';
import { useState, useEffect } from 'react';

export function UpdateBanner() {
  const { isUpdateAvailable, applyUpdate } = usePWA();
  const [isDismissed, setIsDismissed] = useState(false);

  // Check if banner was dismissed recently
  useEffect(() => {
    const dismissedUntil = localStorage.getItem('update-dismissed-until');
    if (dismissedUntil && Date.now() < parseInt(dismissedUntil)) {
      setIsDismissed(true);
    }
  }, []);

  // Only auto-apply if there's a real waiting worker
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isUpdateAvailable && !isDismissed) {
        console.log('🔄 [UpdateBanner] App visible with pending update - applying silently');
        localStorage.removeItem('update-dismissed-until');
        applyUpdate();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isUpdateAvailable, isDismissed, applyUpdate]);

  const handleDismiss = () => {
    console.log('⏸️ [UpdateBanner] Update dismissed by user');
    const dismissedUntil = Date.now() + (24 * 60 * 60 * 1000); // 24h
    localStorage.setItem('update-dismissed-until', dismissedUntil.toString());
    setIsDismissed(true);
  };

  const handleUpdate = () => {
    console.log('✅ [UpdateBanner] Update confirmed by user');
    localStorage.removeItem('update-dismissed-until'); // Clear dismiss state
    applyUpdate();
  };

  if (!isUpdateAvailable || isDismissed) return null;

  return (
    <div 
      className="fixed top-0 left-0 right-0 z-50 
                 bg-[hsl(0,0%,20%)] text-white 
                 border-b border-[hsl(0,0%,30%)]
                 shadow-lg 
                 animate-in slide-in-from-top duration-300"
      role="alert"
      aria-live="polite"
    >
      <div className="container mx-auto px-4 py-3">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          {/* Left: Icon + Message */}
          <div className="flex items-center gap-3">
            <Info className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
            <p className="text-sm font-medium">
              New version available - Update now?
            </p>
          </div>

          {/* Right: Action Buttons */}
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <Button 
              onClick={handleUpdate}
              size="sm" 
              variant="secondary"
              className="flex-1 sm:flex-none"
            >
              Update
            </Button>
            <Button 
              onClick={handleDismiss}
              size="sm" 
              variant="ghost"
              className="flex-1 sm:flex-none text-white hover:bg-white/10"
            >
              Later
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
